import * as React from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState, useRef } from "react";
import Box from "components/common/Box";
import Text from "components/common/Text";
import Separator from "components/common/Separator";
import { formatHelper } from "helper/format";
import RiskProfileGraph from "components/Profile/RiskProfileGraph";
import BlurWrapper from "components/common/BlurWrapper";
import LockedTableWrapper from "components/common/LockTableWrapper";
import { usePhcTheme } from "../PhcThemeContext";

const STATUS_STYLES = {
  "TOP WINNER": { color: "#45AC70", bg: "rgba(69,172,112,0.2)" },
  WINNER: { color: "#45AC70", bg: "rgba(69,172,112,0.2)" },
  FLAT: { color: "#78908F", bg: "rgba(120,144,143,0.2)" },
  LAGGARD: { color: "#F6A823", bg: "rgba(246,168,35,0.2)" },
  DRAG: { color: "#C40000", bg: "rgba(196,0,0,0.2)" },
  "BIGGEST DRAG": { color: "#C40000", bg: "rgba(196,0,0,0.4)" },
};

const resolveStockStatus = (row) => {
  const fromApi =
    row?.status ||
    row?.stock_status ||
    row?.performance_status ||
    row?.remark;
  if (fromApi) return String(fromApi).trim().toUpperCase();

  const pct = Number(
    row?.gain_loss_pct ?? row?.gain_loss_percentage ?? row?.gainLossPct,
  );
  if (Number.isNaN(pct)) return null;
  if (pct >= 20) return "TOP WINNER";
  if (pct > 2) return "WINNER";
  if (pct >= -2) return "FLAT";
  if (pct >= -10) return "LAGGARD";
  if (pct >= -25) return "DRAG";
  return "BIGGEST DRAG";
};

const getRiskMeta = (score) => {
  if (score >= 0 && score <= 4) {
    return {
      text: "Low Risk",
      color: "#2CD500",
      border: "1px solid #2CD500",
      bg: "rgba(44,213,0,0.08)",
    };
  }

  if (score > 4 && score < 7) {
    return {
      text: "Medium Risk",
      color: "#EBA802",
      border: "1px solid #EBA802",
      bg: "rgba(235,168,2,0.08)",
    };
  }

  return {
    text: "High Risk",
    color: "#E24012",
    border: "1px solid #E24012",
    bg: "rgba(226,64,18,0.08)",
  };
};

const nameCellContainer = {
  display: "flex",
  height: "100%",
  flexDirection: "column",
  alignItems: "baseline",
  justifyContent: "center",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  maxWidth: "100%",
};

const valueCellContainer = {
  display: "flex",
  height: "100%",
  flexDirection: "column",
  alignItems: "baseline",
  justifyContent: "center",
  maxWidth: "100%",
};

const MobileStockCard = ({ row, shouldShowOverlay, componentState }) => {
  const { theme } = usePhcTheme();
  
  const status = resolveStockStatus(row);
  const statusStyle = status ? STATUS_STYLES[status] || { color: theme.textMuted, bg: "rgba(120,144,143,0.2)" } : null;
  
  const riskScore = row?.risk_score;
  const riskMeta = riskScore != null ? getRiskMeta(Number(riskScore)) : null;
  const riskLabel = row?.risk_label || riskMeta?.text;

  return (
    <Box sx={{
      border: `1px solid ${theme.sectionBorder}`,
      borderRadius: "16px",
      p: "16px",
      mb: 2,
      bgcolor: "transparent",
      position: "relative"
    }}>
      {/* Name and Action */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", pr: 2 }}>
          <Text sx={{ fontSize: "16px", fontWeight: 500, color: theme.textPrimary, lineHeight: 1.2 }}>{row.name}</Text>
          <Text sx={{ fontSize: "12px", color: theme.textSecondary }}>{row.sector || row.symbol || "—"}</Text>
        </Box>
        {status && (
          <Box sx={{
            px: "10px", py: "4px", borderRadius: "20px", bgcolor: statusStyle.bg, whiteSpace: "nowrap"
          }}>
            <Text sx={{ fontSize: "12px", fontWeight: 500, color: statusStyle.color }}>
              {status}
            </Text>
          </Box>
        )}
      </Box>

      {/* Stats Grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
        <Box>
          <Text sx={{ fontSize: "12px", color: theme.textSecondary, mb: "4px" }}>Weight</Text>
          <Text sx={{ fontSize: "14px", fontWeight: 500, color: theme.textPrimary }}>
            {row.weight ?? row.weightage ?? "-"}%
          </Text>
        </Box>
        <Box>
          <Text sx={{ fontSize: "12px", color: theme.textSecondary, mb: "4px" }}>Value</Text>
          <Text sx={{ fontSize: "14px", fontWeight: 500, color: theme.textPrimary }}>
            {formatHelper.currency(row.current_value, { config: { minimumFractionDigits: 2, maximumFractionDigits: 2 }})}
          </Text>
        </Box>
        <Box>
          <Text sx={{ fontSize: "12px", color: theme.textSecondary, mb: "4px" }}>Risk</Text>
          <Box sx={{ filter: shouldShowOverlay ? "blur(4px)" : "none", display: "flex", alignItems: "center", gap: "6px" }}>
            {riskScore != null ? (
              <>
                <Text sx={{ fontSize: "14px", fontWeight: 500, color: riskMeta.color }}>{riskScore}</Text>
                <Text sx={{ fontSize: "10px", color: riskMeta.color, border: riskMeta.border, px: "6px", py: "2px", borderRadius: "10px", bgcolor: riskMeta.bg, whiteSpace: "nowrap" }}>{riskLabel}</Text>
              </>
            ) : (
              <Text sx={{ fontSize: "14px", color: theme.textMuted }}>-</Text>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const StockAllocationTable = ({
  stockAllocation = [],
  isLoading,
  componentState,
}) => {
  const { theme } = usePhcTheme();
  const isMobile = useMediaQuery((muiTheme) => muiTheme.breakpoints.down('md'));
  const [rows, setRows] = useState([]);

  const tableContainerRef = useRef(null);
  const classes = useStyles({
    tableHeaderSolid: theme.tableHeaderSolid || "rgba(0,126,240,0.2)",
    tableHeaderText: theme.tableHeaderText || theme.textPrimary,
    tableRowBorder: theme.tableRowBorder || "#393939",
  });

  useEffect(() => {
    setRows(stockAllocation || []);
  }, [stockAllocation]);

  if (!Array.isArray(stockAllocation)) {
    console.error("Expected array:", stockAllocation);
    return null;
  }

  const shouldShowOverlay =
    componentState === "blur" || componentState === "locked";

  const columns = [
    {
      field: "name",
      headerText: "Share/ Stock Name",
      bodyCellComponent: (params, row) => {
        const legendText = row?.name?.split(" ");
        let formattedText;

        if (legendText?.length >= 3) {
          formattedText = `${legendText.slice(0, 4).join(" ")}<br/>${legendText
            .slice(4)
            .join(" ")}`;
        } else {
          formattedText = row?.name || "No Data";
        }

        return (
          <div style={nameCellContainer}>
            <Text
              sx={{
                mb: "8px",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                color: theme.textPrimary,
              }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: formattedText?.replace(/\n/g, "<br/>"),
                }}
              />
            </Text>

            <Text
              sx={{
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "18px",
                color: theme.textSecondary,
                mb: "10px",
              }}
            >
              {row.symbol || "—"}
            </Text>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: "6px",
              }}
            >
              {!!row.market_cap && (
                <Text
                  sx={{
                    border: "0.75px solid rgba(229,229,229,0.55)",
                    fontWeight: 400,
                    fontSize: "10px",
                    lineHeight: "13px",
                    color: theme.textPrimary,
                    px: "6px",
                    py: "4px",
                    borderRadius: "12px",
                    whiteSpace: "nowrap",
                    background:
                      "linear-gradient(146.91deg, rgba(61,61,61,0.5) 6.18%, rgba(170,170,170,0) 78.87%)",
                  }}
                >
                  {row.market_cap}
                </Text>
              )}
              {!!row.sector && (
                <Text
                  sx={{
                    border: "0.75px solid rgba(229,229,229,0.55)",
                    fontWeight: 400,
                    fontSize: "10px",
                    lineHeight: "13px",
                    color: theme.textPrimary,
                    px: "6px",
                    py: "4px",
                    borderRadius: "12px",
                    whiteSpace: "nowrap",
                    background:
                      "linear-gradient(146.91deg, rgba(61,61,61,0.5) 6.18%, rgba(170,170,170,0) 78.87%)",
                  }}
                >
                  {row.sector}
                </Text>
              )}
            </Box>
          </div>
        );
      },
    },
    {
      field: "risk_score",
      headerText: "Risk Score",
      bodyCellComponent: (params, row) => {
        const riskScore = row?.risk_score;
        if (riskScore == null) {
          return <Text sx={{ color: theme.textMuted }}>No Data</Text>;
        }

        const riskMeta = getRiskMeta(Number(riskScore));
        const riskLabel = row?.risk_label || riskMeta.text;

        return (
          <div className="risk-score-cell">
            <div style={{ filter: shouldShowOverlay ? "blur(4px)" : "none" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "8px",
                  mt: "2px",
                  minHeight: "42px",
                }}
              >
                <RiskProfileGraph
                  riskProfileId={riskScore}
                  chartWidth={58}
                  disableLabel={true}
                  useMax10={true}
                  needleWidth={3}
                  needleLenght={18}
                />
                <Text
                  sx={{
                    color: riskMeta.color,
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  {riskScore}
                </Text>
              </Box>

              <Separator size={1} />

              <Text
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 400,
                  fontSize: "11px",
                  lineHeight: "14px",
                  px: "10px",
                  py: "6px",
                  border: riskMeta.border,
                  borderRadius: "20px",
                  color: riskMeta.color,
                  bgcolor: riskMeta.bg,
                }}
              >
                {riskLabel}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      field: "buy_date",
      headerText: "Buy Date",
      bodyCellComponent: (params, row) => (
        <div style={valueCellContainer}>
          <Text sx={{ fontSize: "16px", color: theme.textPrimary, whiteSpace: "nowrap" }}>
            {row.buy_date || "-"}
          </Text>
        </div>
      ),
    },
    {
      field: "quantity",
      headerText: "Qty.",
      bodyCellComponent: (params, row) => (
        <div style={valueCellContainer}>
          <Text sx={{ fontSize: "16px", color: theme.textPrimary }}>
            {row.quantity || "-"}
          </Text>
        </div>
      ),
    },
    {
      field: "weightage",
      headerText: "Wtg.",
      bodyCellComponent: (params, row) => (
        <div
          style={{
            ...valueCellContainer,
            alignItems: "center",
          }}
        >
          <Text sx={{ fontSize: "16px", color: theme.textPrimary }}>
            {row.weight ?? row.weightage ?? "-"}%
          </Text>
        </div>
      ),
    },
    {
      field: "buy_price",
      headerText: "Buy Price & Value",
      renderHeader: () => <div style={{ fontWeight: 500 }}>Buy Price & Value</div>,
      bodyCellComponent: (params, row) => (
        <div style={{ ...valueCellContainer, gap: "6px" }}>
          <Text sx={{ fontSize: "14px", color: theme.textPrimary, whiteSpace: "nowrap" }}>
            {formatHelper.currency(row.buy_price, {
              config: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
            })}
          </Text>
          <Text sx={{ fontWeight: 400, fontSize: "14px", color: theme.textSecondary, whiteSpace: "nowrap" }}>
            {formatHelper.currency(row.buy_value, {
              config: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
            })}
          </Text>
        </div>
      ),
    },
    {
      field: "current_price",
      headerText: "Current Price & Value",
      renderHeader: () => (
        <div style={{ fontWeight: 500 }}>Current Price & Value</div>
      ),
      bodyCellComponent: (params, row) => (
        <div style={{ ...valueCellContainer, gap: "6px" }}>
          <Text sx={{ fontSize: "14px", color: theme.textPrimary, whiteSpace: "nowrap" }}>
            {formatHelper.currency(row.current_price, {
              config: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
            })}
          </Text>
          <Text sx={{ fontWeight: 400, fontSize: "14px", color: theme.textSecondary, whiteSpace: "nowrap" }}>
            {formatHelper.currency(row.current_value, {
              config: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
            })}
          </Text>
        </div>
      ),
    },
   
    {
      field: "gain_loss",
      headerText: "Gain/ Loss",
      bodyCellComponent: (params, row) => {
        const numericValue = Number(row?.gain_loss);
        const isPositive = numericValue > 0;
        const color = isPositive ? "#00E676" : numericValue < 0 ? "#FF0000" : theme.textPrimary;
        const formattedValue = formatHelper.currency(numericValue, {
          config: {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        });

        const pctRaw =
          row?.gain_loss_pct ??
          row?.gain_loss_percentage ??
          row?.gainLossPct;
        const pctNum =
          pctRaw == null || pctRaw === "" ? null : Number(pctRaw);
        const pctLabel =
          pctNum == null || Number.isNaN(pctNum)
            ? "—"
            : `${pctNum > 0 ? "+" : ""}${pctNum}%`;

        return (
          <div style={{ ...valueCellContainer, gap: "6px", color }}>
            <Text sx={{ fontSize: "14px", color }}>
              {Number.isNaN(numericValue)
                ? "—"
                : isPositive
                  ? `+${formattedValue}`
                  : formattedValue}
            </Text>
            <Text sx={{ fontWeight: 400, fontSize: "14px", color }}>
              {pctLabel}
            </Text>
          </div>
        );
      },
    },
    {
      field: "status",
      headerText: "Status",
      bodyCellComponent: (params, row) => {
        const status = resolveStockStatus(row);
        if (!status) {
          return <Text sx={{ color: theme.textMuted, fontSize: "14px" }}>—</Text>;
        }

        const style = STATUS_STYLES[status] || {
          color: theme.textMuted,
          bg: "rgba(120,144,143,0.2)",
        };

        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              px: "8px",
              py: "6px",
              borderRadius: "30px",
              bgcolor: style.bg,
              whiteSpace: "nowrap",
            }}
          >
            <Text
              sx={{
                fontWeight: 500,
                fontSize: "12px",
                color: style.color,
                lineHeight: "normal",
              }}
            >
              {status}
            </Text>
          </Box>
        );
      },
    },
  ];

  const OverlayComponent =
    componentState === "blur" ? BlurWrapper : LockedTableWrapper;

  return (
    <Box
      sx={{
        position: "relative",
        mt: "10px",
        backgroundColor: theme.cardBg,
        border: `1px solid ${theme.sectionBorder}`,
        backgroundImage: theme.sectionBg === "#FFFFFF" ? "none" : theme.sectionBg,
        borderRadius: "20px",
        boxShadow:
          "0px -6px 6px -1px rgba(0,0,0,0.1), 0px -8px 4px -2px rgba(0,0,0,0.1)",
        px: { xs: 2, md: "27px" },
        pt: { xs: 2.5, md: "24px" },
        pb: { xs: 2, md: "14px" },
        overflow: "hidden",
      }}
    >
      <Text
        sx={{
          fontWeight: 700,
          fontSize: { xs: "20px", md: "24px" },
          lineHeight: "normal",
          letterSpacing: "-0.72px",
          color: theme.textPrimary,
          mb: { xs: "18px", md: "26px" },
        }}
      >
        Stock Allocation
      </Text>

      {isMobile ? (
        <Box sx={{ position: "relative" }}>
          {rows.map((row, index) => (
            <MobileStockCard 
              key={index} 
              row={row} 
              shouldShowOverlay={shouldShowOverlay} 
              componentState={componentState} 
            />
          ))}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            position: "relative",
            background: "transparent",
            boxShadow: "none",
            borderRadius: 0,
            color: theme.textPrimary,
          }}
          ref={tableContainerRef}
        >
          <MuiTable stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.field} className={classes.header}>
                    {col.renderHeader ? col.renderHeader() : col.headerText}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((col) => (
                    <TableCell key={col.field} className={classes.bodyCell}>
                      {col.bodyCellComponent
                        ? col.bodyCellComponent(null, row)
                        : (row[col.field] ?? "-")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>
      )}

      {shouldShowOverlay && (
        <Box
          sx={{
            position: "absolute",
            top: { xs: "52%", sm: "56%", md: "58%" },
            // Mobile: pin to Risk Score column (right). Desktop: center on col 2.
            left: { xs: "auto", sm: "34%", md: "26%" },
            right: { xs: "10px", sm: "auto", md: "auto" },
            transform: {
              xs: "translateY(-50%)",
              sm: "translate(-50%, -50%)",
              md: "translate(-50%, -50%)",
            },
            width: { xs: "132px", sm: "140px", md: "150px" },
            maxWidth: { xs: "38%", sm: "140px", md: "150px" },
            height: "auto",
            zIndex: 2,
            pointerEvents: "auto",
          }}
        >
          <OverlayComponent
            blur={componentState === "blur"}
            locked={componentState === "locked"}
          />
        </Box>
      )}
    </Box>
  );
};

export default StockAllocationTable;

const useStyles = makeStyles({
  header: (props) => ({
    backgroundColor: `${props.tableHeaderSolid} !important`,
    color: `${props.tableHeaderText} !important`,
    fontWeight: 500,
    fontSize: "14px",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${props.tableRowBorder} !important`,
    padding: "10px 12px 16px 0",
  }),
  bodyCell: (props) => ({
    padding: "16px 12px 16px 0",
    position: "relative",
    borderBottom: `1px solid ${props.tableRowBorder} !important`,
    color: "inherit !important",
    verticalAlign: "top",
    backgroundColor: "transparent",
  }),
});
